import {
  Box,
  ChevronDown,
  ChevronUp,
  Flex,
  Heading,
  Information,
  Pressable,
  Text,
} from "../../../../../../mads";
import { LinearGradient as ExpoLinearGradient } from "../../../../../../shared/components/LinearGradient/linear-gradient";
import { RichText } from "../../../../../../shared/components/RichText";
import { useI18n } from "../../../../../../shared/I18n";
import { logTapButtonWithExtraParams } from "../../../../../../shared/native-modules/tare/logTapButtonWithExtraParams";
import { useViewJobDataContext } from "../../../../context/ViewJobDataContext";
import { ViewJobDivider } from "../../Divider";

import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import MaskedView from "@react-native-masked-view/masked-view";
import { useNavigation } from "@react-navigation/native";
import React, { useCallback, useId, useRef, useState } from "react";
import { Animated, Easing, type ViewStyle } from "react-native";
import Svg, { LinearGradient, Path, Stop } from "react-native-svg";

const COLLAPSED_HEIGHT = 160;
const MAX_EXPANDED_HEIGHT = 2000;
const FADE_HEIGHT = 120;

const fadeOverlayStyle: ViewStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: FADE_HEIGHT,
};

function AIDisclaimerButton() {
  const i18n = useI18n();
  const navigation = useNavigation();

  return (
    <Pressable
      onPress={() => navigation.navigate("CompanySummaryDisclaimerModal" as never)}
      accessibilityRole="button"
      accessibilityLabel={i18n.pgettext(
        "AI company summary info button",
        "More info about AI-generated summary",
      )}
      sx={{ marginInlineStart: "auto", marginBlockStart: 2 }}
    >
      <Information size="xs" variant="stroke" fill={colors.neutral1000} aria-hidden />
    </Pressable>
  );
}

function AIGeneralGradientIcon({ large = true }: { large?: boolean }) {
  const gradientId = `ai-gradient-${useId()}`;

  if (large) {
    const gradient = (
      <LinearGradient
        id={gradientId}
        x1="2"
        y1="2"
        x2="22.0001"
        y2="22.0001"
        gradientUnits="userSpaceOnUse"
      >
        <Stop offset="0.25" stopColor="#003A9B" />
        <Stop offset="0.75" stopColor="#358271" />
      </LinearGradient>
    );
    return (
      <Svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden>
        <Path
          d="M16.6484 12.2578C16.7562 11.914 17.2438 11.9141 17.3516 12.2578L18.1152 14.7002C18.292 15.2654 18.7346 15.708 19.2998 15.8848L21.7422 16.6484C22.0861 16.7562 22.0861 17.2438 21.7422 17.3516L19.2998 18.1152C18.7346 18.292 18.292 18.7346 18.1152 19.2998L17.3516 21.7422C17.2438 22.0861 16.7562 22.0861 16.6484 21.7422L15.8848 19.2998C15.708 18.7346 15.2654 18.292 14.7002 18.1152L12.2578 17.3516C11.9141 17.2438 11.914 16.7562 12.2578 16.6484L14.7002 15.8848C15.2654 15.708 15.708 15.2654 15.8848 14.7002L16.6484 12.2578Z"
          fill={`url(#${gradientId})`}
        />
        <Path
          d="M8.50781 2.36133C8.65852 1.87955 9.34146 1.87957 9.49219 2.36133L10.5615 5.78027C10.809 6.57152 11.4285 7.19098 12.2197 7.43848L15.6387 8.50781C16.1205 8.65849 16.1205 9.3415 15.6387 9.49219L12.2197 10.5615C11.4285 10.809 10.809 11.4285 10.5615 12.2197L9.49219 15.6387C9.3415 16.1205 8.65849 16.1205 8.50781 15.6387L7.43848 12.2197C7.19098 11.4285 6.57152 10.809 5.78027 10.5615L2.36133 9.49219C1.87957 9.34146 1.87955 8.65852 2.36133 8.50781L5.78027 7.43848C6.57152 7.19099 7.19099 6.57152 7.43848 5.78027L8.50781 2.36133Z"
          fill={`url(#${gradientId})`}
        />
        {gradient}
      </Svg>
    );
  }

  const gradient = (
    <LinearGradient
      id={gradientId}
      x1="0"
      y1="0"
      x2="16.6667"
      y2="16.6667"
      gradientUnits="userSpaceOnUse"
    >
      <Stop offset="0.25" stopColor="#003A9B" />
      <Stop offset="0.75" stopColor="#358271" />
    </LinearGradient>
  );
  return (
    <Svg width={17} height={17} viewBox="0 0 17 17" fill="none" aria-hidden>
      <Path
        d="M12.207 8.54818C12.2969 8.2617 12.7031 8.26171 12.793 8.54818L13.4294 10.5835C13.5767 11.0545 13.9455 11.4233 14.4165 11.5706L16.4518 12.207C16.7384 12.2968 16.7384 12.7032 16.4518 12.793L14.4165 13.4294C13.9455 13.5767 13.5767 13.9455 13.4294 14.4165L12.793 16.4518C12.7032 16.7384 12.2968 16.7384 12.207 16.4518L11.5706 14.4165C11.4233 13.9455 11.0545 13.5767 10.5835 13.4294L8.54818 12.793C8.26171 12.7031 8.2617 12.2969 8.54818 12.207L10.5835 11.5706C11.0545 11.4233 11.4233 11.0545 11.5706 10.5835L12.207 8.54818Z"
        fill={`url(#${gradientId})`}
      />
      <Path
        d="M5.42318 0.30111C5.54877 -0.100376 6.11789 -0.100363 6.24349 0.30111L7.13461 3.15023C7.34085 3.8096 7.85707 4.32582 8.51644 4.53207L11.3656 5.42318C11.7671 5.54874 11.7671 6.11792 11.3656 6.24349L8.51644 7.13461C7.85707 7.34085 7.34085 7.85707 7.13461 8.51644L6.24349 11.3656C6.11792 11.7671 5.54874 11.7671 5.42318 11.3656L4.53207 8.51644C4.32582 7.85707 3.8096 7.34085 3.15023 7.13461L0.30111 6.24349C-0.100364 6.11789 -0.100376 5.54877 0.30111 5.42318L3.15023 4.53207C3.8096 4.32582 4.32582 3.8096 4.53207 3.15023L5.42318 0.30111Z"
        fill={`url(#${gradientId})`}
      />
      {gradient}
    </Svg>
  );
}

interface CompanySummaryProps {
  companySummaryText: string;
  withPremiumSponsorship?: boolean;
}

export function CompanySummary({
  companySummaryText,
  withPremiumSponsorship = false,
}: CompanySummaryProps) {
  const i18n = useI18n();
  const { initialData } = useViewJobDataContext();
  const fccKey = initialData?.companyTabModel?.fccKey;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const maxHeightAnim = useRef(new Animated.Value(COLLAPSED_HEIGHT)).current;
  const fadeOpacity = useRef(new Animated.Value(1)).current;

  const match = companySummaryText.match(/^\*\*(.*?)\*\*\n\n([\s\S]*)$/);
  const [containerWidth, setContainerWidth] = useState(0);
  const [textHeight, setTextHeight] = useState(0);
  const [naturalTextWidth, setNaturalTextWidth] = useState(0);

  const handleToggle = useCallback(() => {
    const expanding = isCollapsed;
    setIsCollapsed(!expanding);

    logTapButtonWithExtraParams(
      "rn-view-job-employer-insights",
      "company-summary-toggle",
      ["viewjob"],
      { expanded: expanding },
    ).catch(console.error);

    if (expanding) {
      Animated.parallel([
        Animated.timing(maxHeightAnim, {
          toValue: MAX_EXPANDED_HEIGHT,
          duration: 300,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
        Animated.timing(fadeOpacity, {
          toValue: 0,
          duration: 200,
          delay: 0,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(maxHeightAnim, {
          toValue: COLLAPSED_HEIGHT,
          duration: 300,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
        Animated.timing(fadeOpacity, {
          toValue: 1,
          duration: 200,
          delay: 100,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
      ]).start();
    }
  }, [isCollapsed, maxHeightAnim, fadeOpacity]);

  if (!match) {
    console.error(
      `CompanySummary: malformed summary text could not be parsed for fccKey=${fccKey}`,
    );
    return null;
  }

  const heading = match[1];
  const body = match[2];
  const headingLevel = withPremiumSponsorship ? 2 : 4;
  const headingMargin = withPremiumSponsorship ? spacing.s3 : spacing.s4;
  const headingContainerTopMargin = withPremiumSponsorship ? -1 : -3;
  const buttonTopSpacing = isCollapsed ? 0 : spacing.s2;

  return (
    <Box>
      <Flex sx={{ flexDirection: "row", alignItems: "flex-start", marginBlockEnd: headingMargin }}>
        <AIGeneralGradientIcon large={!withPremiumSponsorship} />
        <Box
          sx={{
            flex: 1,
            marginInlineStart: spacing.s1,
            marginBlockStart: headingContainerTopMargin,
          }}
          onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
          testID="company-summary-heading-container"
        >
          {/* used for measuring wrapped height at container width — used as MaskedView height */}
          <Box
            sx={{ position: "absolute", left: 0, top: 0, right: 0, opacity: 0 }}
            onLayout={(e) => setTextHeight(e.nativeEvent.layout.height)}
            testID="heading-height-measurement"
            aria-hidden
          >
            <Heading level={headingLevel} sx={{ marginBlock: 0 }}>
              {heading}
            </Heading>
          </Box>
          {/* used for measuring unconstrained single-line width — used to size gradient to text, not full container */}
          <Box
            sx={{ position: "absolute", opacity: 0 }}
            onLayout={(e) => setNaturalTextWidth(e.nativeEvent.layout.width)}
            testID="heading-width-measurement"
            aria-hidden
          >
            <Heading level={headingLevel} sx={{ marginBlock: 0 }}>
              {heading}
            </Heading>
          </Box>
          {containerWidth > 0 && textHeight > 0 && naturalTextWidth > 0 && (
            <MaskedView
              testID="company-summary-masked-view"
              style={{ width: Math.min(naturalTextWidth, containerWidth), height: textHeight }}
              maskElement={
                <Box sx={{ width: Math.min(naturalTextWidth, containerWidth), height: textHeight }}>
                  <Heading
                    level={headingLevel}
                    sx={{
                      marginBlock: 0,
                      position: "absolute",
                      left: 0,
                      top: 0,
                      right: 0,
                    }}
                  >
                    {heading}
                  </Heading>
                </Box>
              }
            >
              <ExpoLinearGradient
                colors={["#003A9B", "#358271"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{ width: Math.min(naturalTextWidth, containerWidth), height: textHeight }}
              />
            </MaskedView>
          )}
        </Box>
        <AIDisclaimerButton />
      </Flex>
      <Animated.View style={{ maxHeight: maxHeightAnim, overflow: "hidden", position: "relative" }}>
        <RichText
          content={body}
          level={2}
          sx={{ color: colors.neutral1000, marginBlockEnd: buttonTopSpacing }}
        />
        <Animated.View style={[fadeOverlayStyle, { opacity: fadeOpacity }]} pointerEvents="none">
          <ExpoLinearGradient
            colors={["rgba(255, 255, 255, 0.0)", "rgba(255, 255, 255, 1.0)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={{ flex: 1 }}
            pointerEvents="none"
          />
        </Animated.View>
      </Animated.View>
      <Pressable
        onPress={handleToggle}
        accessibilityRole="button"
        accessibilityLabel={isCollapsed ? i18n.gettext("Show more") : i18n.gettext("Show less")}
        sx={{
          alignSelf: "flex-start",
          flexDirection: "row",
          alignItems: "center",
          marginBlockEnd: spacing.s4,
        }}
      >
        <Text
          level={3}
          sx={{
            color: colors.neutral800,
            borderWidth: 0,
            borderBottomWidth: 1,
            borderStyle: "dotted",
            marginBlockStart: 0,
          }}
        >
          {isCollapsed ? i18n.gettext("Show more") : i18n.gettext("Show less")}
        </Text>
        <Box sx={{ marginBlockStart: 8 }}>
          {isCollapsed ? (
            <ChevronDown size="sm" color={colors.neutral800} />
          ) : (
            <ChevronUp size="sm" color={colors.neutral800} />
          )}
        </Box>
      </Pressable>
      <ViewJobDivider />
    </Box>
  );
}
